import { useBlocker } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import type {
  EstimationModelResponse,
  ProjectWithEstimationResponse,
} from '@/api/schema/data-contracts';
import { ApiEstimationAdditionalFieldType } from '@/api/schema/data-contracts';
import { Button } from '@/components/atoms/Button';
import { Stack } from '@/components/atoms/Stack';
import { ModalStateless } from '@/components/ModalStateless';
import { useMessages } from '@/hooks/messages';
import { useAutosaveOnBlur } from '@/hooks/useAutosaveOnBlur';

import { metadataDefinitions } from './messages';
import { MetadataEditation } from './MetadataEditation';
import type { AdditionalField, AdditionalFieldsFormModel } from './model';

const AUTOSAVE_DEBOUNCE_MS = 300;

interface MetadataFormProps {
  estimationModel: EstimationModelResponse;
  additionalFields: AdditionalField[];
  projectWithEstimation: ProjectWithEstimationResponse;
  onSubmit: (formModel: AdditionalFieldsFormModel) => Promise<void>;
  onProjectDescriptionSubmit: (projectDescription: string) => Promise<void>;
  isReadOnly: boolean;
}

export const MetadataForm: FC<MetadataFormProps> = ({
  estimationModel,
  additionalFields,
  projectWithEstimation,
  onSubmit,
  onProjectDescriptionSubmit,
  isReadOnly,
}) => {
  const messages = useMessages(metadataDefinitions);
  const [isSavingOnLeave, setIsSavingOnLeave] = useState(false);
  const [hasValidationError, setHasValidationError] = useState(false);

  const formModelInitial = useMemo<AdditionalFieldsFormModel>(
    () => ({
      fields: additionalFields.map((x) => ({
        estimationModelAdditionalFieldDefinitionId: x.estimationModelAdditionalFieldDefinitionId,
        fieldType: x.fieldType,
        value:
          x.fieldType === ApiEstimationAdditionalFieldType.DATE && x.value !== null
            ? dayjs(x.value).toDate()
            : x.value,
      })),
    }),
    [additionalFields],
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isDirty: isAdditionalFieldsDirty },
  } = useForm<AdditionalFieldsFormModel>({
    mode: 'onChange',
    defaultValues: formModelInitial,
  });

  // Form for Project Description
  const {
    control: projectDescriptionControl,
    reset: resetProjectDescription,
    getValues: getProjectDescriptionValues,
    trigger: triggerProjectDescription,
    formState: { isDirty: isProjectDescriptionDirty },
  } = useForm<{ projectDescription: string }>({
    mode: 'onChange',
    defaultValues: {
      projectDescription: projectWithEstimation.estimation.projectDescription ?? '',
    },
  });

  useEffect(() => {
    reset(formModelInitial);
  }, [formModelInitial, reset]);

  useEffect(() => {
    if (projectWithEstimation?.estimation) {
      resetProjectDescription({
        projectDescription: projectWithEstimation.estimation.projectDescription ?? '',
      });
    }
  }, [projectWithEstimation.estimation, resetProjectDescription]);

  const combinedOnSubmit = useCallback(
    async (formModel: AdditionalFieldsFormModel) => {
      // Only PATCH additional fields when they actually changed — mirrors the
      // project-description guard below so neither form fires a redundant call.
      if (isAdditionalFieldsDirty) {
        await onSubmit(formModel);
      }
      const projectDescriptionValue = getProjectDescriptionValues().projectDescription;
      if (projectDescriptionValue !== (projectWithEstimation.estimation.projectDescription ?? '')) {
        await onProjectDescriptionSubmit(projectDescriptionValue);
      }
    },
    [
      isAdditionalFieldsDirty,
      onSubmit,
      onProjectDescriptionSubmit,
      getProjectDescriptionValues,
      projectWithEstimation.estimation.projectDescription,
    ],
  );

  const { onBlur: onFieldBlur } = useAutosaveOnBlur(() => {
    if (isReadOnly) return;
    void triggerProjectDescription('projectDescription').then((projectDescValid) => {
      if (!projectDescValid) return;
      void handleSubmit(combinedOnSubmit)();
    });
  }, AUTOSAVE_DEBOUNCE_MS);

  // Block navigation when the form is dirty (unsaved changes).
  // withResolver: true exposes status/proceed/reset so we can render our own dialog
  // instead of the browser's native confirm(), matching the ConfirmationModal pattern
  // already established elsewhere in the codebase.
  const isDirty = !isReadOnly && (isAdditionalFieldsDirty || isProjectDescriptionDirty);
  const blocker = useBlocker({
    shouldBlockFn: () => isDirty,
    withResolver: true,
    enableBeforeUnload: isDirty,
  });

  const handleSaveAndLeave = useCallback(async () => {
    if (blocker.status !== 'blocked') return;
    setIsSavingOnLeave(true);
    try {
      // Validate both forms up front. The field-level error messages render on
      // the inputs behind the modal overlay, so on failure we surface an
      // explicit message inside the modal instead of leaving the click with no
      // visible effect (EWM-877).
      const [fieldsValid, projectDescriptionValid] = await Promise.all([
        trigger(),
        triggerProjectDescription('projectDescription'),
      ]);
      if (!fieldsValid || !projectDescriptionValid) {
        setHasValidationError(true);
        return;
      }
      setHasValidationError(false);
      // handleSubmit() resolves without invoking the callback when validation
      // fails, so track whether the save actually ran. Only navigate when the
      // form validated AND the save succeeded — otherwise stay on the page so
      // the user can fix/retry without silently losing their changes.
      let saved = false;
      await handleSubmit(async (formModel) => {
        await combinedOnSubmit(formModel);
        saved = true;
      })();
      if (saved) {
        blocker.proceed();
      }
    } catch {
      // Save failed (network/server error) — keep the user on the page.
    } finally {
      setIsSavingOnLeave(false);
    }
  }, [blocker, handleSubmit, combinedOnSubmit, trigger, triggerProjectDescription]);

  const handleDiscardAndLeave = useCallback(() => {
    if (blocker.status !== 'blocked') return;
    setHasValidationError(false);
    blocker.proceed();
  }, [blocker]);

  const handleCancelLeave = useCallback(() => {
    if (blocker.status !== 'blocked') return;
    setHasValidationError(false);
    blocker.reset();
  }, [blocker]);

  return (
    <>
      <form onSubmit={handleSubmit(combinedOnSubmit)}>
        <Stack gap={5}>
          <MetadataEditation
            control={control}
            estimationModel={estimationModel}
            projectWithEstimation={projectWithEstimation}
            isReadOnly={isReadOnly}
            projectDescriptionControl={projectDescriptionControl}
            onFieldBlur={onFieldBlur}
          />
          {/* Hidden submit for enter key support */}
          <input type='submit' className='hidden' />
        </Stack>
      </form>

      {/* Unsaved-changes guard — shown when the user navigates away with dirty form fields */}
      <ModalStateless
        title={messages.unsavedChangesTitle()}
        open={blocker.status === 'blocked'}
        onClose={handleCancelLeave}
        size='s'
        buttons={
          <div className='flex w-full justify-end gap-3'>
            <Button variant='secondary' onClick={handleCancelLeave} disabled={isSavingOnLeave}>
              {messages.unsavedChangesCancel()}
            </Button>
            <Button
              variant='secondary'
              outlined
              onClick={handleDiscardAndLeave}
              disabled={isSavingOnLeave}
            >
              {messages.unsavedChangesDiscard()}
            </Button>
            <Button
              variant='primary'
              onClick={() => void handleSaveAndLeave()}
              isProcessing={isSavingOnLeave}
              disabled={isSavingOnLeave}
            >
              {messages.unsavedChangesSave()}
            </Button>
          </div>
        }
      >
        <p>{messages.unsavedChangesWarning()}</p>
        {hasValidationError && (
          <p className='text-danger mt-2 text-sm'>{messages.unsavedChangesValidationError()}</p>
        )}
      </ModalStateless>
    </>
  );
};
